import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["row", "sort", "search", "columnFilter", "noResults"]

  static values = {
    sortKey: String,
    sortDirection: { type: String, default: "asc" }
  }

  sort({ currentTarget }) {
    const key = currentTarget.dataset.sortKey
    const type = currentTarget.dataset.sortType || "string"

    if (this.sortKeyValue === key) {
      this.sortDirectionValue = this.sortDirectionValue === "asc" ? "desc" : "asc"
    } else {
      this.sortKeyValue = key
      this.sortDirectionValue = "asc"
    }

    this.updateSortIndicators(currentTarget)

    const rows = [...this.rowTargets]
    const dir = this.sortDirectionValue === "asc" ? 1 : -1

    rows.sort((a, b) => {
      const aVal = this.cellValue(a, key)
      const bVal = this.cellValue(b, key)
      return dir * this.compare(aVal, bVal, type)
    })

    const tbody = rows[0]?.parentElement
    if (tbody) rows.forEach(row => tbody.appendChild(row))
  }

  filter() {
    const query = this.hasSearchTarget ? this.searchTarget.value.toLowerCase().trim() : ""
    const columnFilters = this.activeColumnFilters()
    let visibleCount = 0

    this.rowTargets.forEach(row => {
      const matchesSearch = !query || this.rowMatchesSearch(row, query)
      const matchesColumns = this.rowMatchesColumnFilters(row, columnFilters)
      const visible = matchesSearch && matchesColumns
      row.hidden = !visible
      if (visible) visibleCount++
    })

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.hidden = visibleCount > 0
    }
  }

  // --- Private ---

  cellValue(row, key) {
    const cell = row.querySelector(`[data-sort-key="${key}"]`)
    return cell ? (cell.dataset.sortValue ?? cell.textContent.trim()) : ""
  }

  compare(a, b, type) {
    if (type === "number") return (parseFloat(a) || 0) - (parseFloat(b) || 0)
    if (type === "date") return (new Date(a) || 0) - (new Date(b) || 0)
    return a.localeCompare(b, undefined, { sensitivity: "base" })
  }

  rowMatchesSearch(row, query) {
    const cells = row.querySelectorAll("[data-filter-value]")
    return [...cells].some(cell => cell.dataset.filterValue.toLowerCase().includes(query))
  }

  activeColumnFilters() {
    if (!this.hasColumnFilterTarget) return {}
    const filters = {}
    this.columnFilterTargets.forEach(el => {
      const key = el.dataset.filterKey
      const value = el.value
      if (key && value) filters[key] = value.toLowerCase()
    })
    return filters
  }

  rowMatchesColumnFilters(row, filters) {
    return Object.entries(filters).every(([key, value]) => {
      const cell = row.querySelector(`[data-sort-key="${key}"]`)
      if (!cell) return true
      const cellVal = (cell.dataset.filterValue ?? cell.textContent.trim()).toLowerCase()
      return cellVal === value
    })
  }

  updateSortIndicators(activeHeader) {
    this.sortTargets.forEach(header => {
      header.classList.remove("sort-asc", "sort-desc", "sort-active")
    })
    activeHeader.classList.add("sort-active", `sort-${this.sortDirectionValue}`)
  }
}
